/**
 * Project Control Hub — Project Financial Command Center.
 *
 * A single-page financial cockpit for the CFO/PM. Surfaces the data
 * already produced by the Commitment Engine + Accounting Engine; does
 * NOT create new backend surface. RTL Arabic layout.
 *
 * Sections (top-down):
 *   1. Hero strip — Project Health (status, contract no, EAC vs Budget).
 *   2. Core Financial Stack — Budget / Committed / Actual / Available
 *      (4 KPI cards + master progress bar).
 *   3. Execution Timeline — Award → PO → GRN → Bill → Payment
 *      (horizontal stepper with counts & amounts).
 *   4. Risk Indicators — over-commitment %, unbilled GRN exposure,
 *      pending approvals.
 *   5. Drilldown — Commitments table with movement history popover.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, FileSignature, ShoppingCart, PackageCheck,
  Receipt, Wallet, AlertTriangle, TrendingUp, TrendingDown,
  CheckCircle2, CircleDashed, Loader2, Layers,
} from "lucide-react";

const SAR = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "SAR", maximumFractionDigits: 0,
  }).format(Number(n || 0));

const PCT = (n) => `${Math.round(Number(n || 0))}%`;

const Pill = ({ children, tone = "slate", testid }) => {
  const tones = {
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    green:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    red:     "bg-rose-50 text-rose-700 border-rose-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const KPI = ({ label, value, sub, tone = "slate", testid, icon: Icon }) => {
  const tones = {
    slate:  "from-slate-50 to-white border-slate-200 text-slate-900",
    blue:   "from-blue-50 to-white border-blue-200 text-blue-900",
    amber:  "from-amber-50 to-white border-amber-200 text-amber-900",
    green:  "from-emerald-50 to-white border-emerald-200 text-emerald-900",
    red:    "from-rose-50 to-white border-rose-200 text-rose-900",
    violet: "from-violet-50 to-white border-violet-200 text-violet-900",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border bg-gradient-to-b p-5 shadow-sm ${tones[tone]}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider opacity-70">
          {label}
        </span>
        {Icon ? <Icon className="h-4 w-4 opacity-50" /> : null}
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-xs opacity-70">{sub}</div> : null}
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────

const ProjectControlHub = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [awards, setAwards] = useState([]);
  const [openCommitmentId, setOpenCommitmentId] = useState(null);
  const [movements, setMovements] = useState([]);
  const [aging, setAging] = useState(null);
  const [evm, setEvm] = useState(null);
  // Iter-146 — customer-side cards (revenue) on the Hub
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerReceipts, setCustomerReceipts] = useState([]);

  const refreshAll = useCallback(async () => {
    try {
      const [
        prjRes, sumRes, cmtRes, poRes, billRes,
      ] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}`).catch(() => ({ data: null })),
        axiosInstance.get(`/projects/${projectId}/commitment-summary`)
          .catch(() => ({ data: null })),
        axiosInstance.get(`/commitments?project_id=${projectId}&limit=200`)
          .catch(() => ({ data: { items: [] } })),
        axiosInstance.get(`/purchase-orders?project_id=${projectId}`)
          .catch(() => ({ data: [] })),
        axiosInstance.get(`/supplier-bills?project_id=${projectId}`)
          .catch(() => ({ data: [] })),
      ]);
      setProject(prjRes.data);
      setSummary(sumRes.data);
      setCommitments((cmtRes.data && cmtRes.data.items) || []);
      const posList = Array.isArray(poRes.data) ? poRes.data
        : (poRes.data?.items || poRes.data?.purchase_orders || []);
      setPos(posList);
      const billList = Array.isArray(billRes.data) ? billRes.data
        : (billRes.data?.items || billRes.data?.bills || []);
      setBills(billList);
      // GRNs & Payments & Awards — best-effort fetches, may not support filter.
      const poIds = new Set(posList.map((p) => p.id));
      try {
        const grnRes = await axiosInstance.get("/grns?limit=500");
        const all = Array.isArray(grnRes.data) ? grnRes.data
          : (grnRes.data?.items || []);
        setGrns(all.filter((g) =>
          poIds.has(g.purchase_order_id) || g.project_id === projectId));
      } catch { setGrns([]); }
      try {
        const billIds = new Set(billList.map((b) => b.id));
        const payRes = await axiosInstance.get("/bill-payments?limit=500");
        const all = Array.isArray(payRes.data) ? payRes.data
          : (payRes.data?.items || []);
        setPayments(all.filter((p) => billIds.has(p.bill_id)));
      } catch { setPayments([]); }
      try {
        const awRes = await axiosInstance.get(`/awards?project_id=${projectId}`);
        const all = Array.isArray(awRes.data) ? awRes.data
          : (awRes.data?.awards || awRes.data?.items || []);
        setAwards(all);
      } catch { setAwards([]); }
      try {
        const agingRes = await axiosInstance.get(
          `/accounting/gr-ir-aging?project_id=${projectId}`,
        );
        setAging(agingRes.data);
      } catch { setAging(null); }
      try {
        const evmRes = await axiosInstance.get(`/projects/${projectId}/evm`);
        setEvm(evmRes.data);
      } catch { setEvm(null); }
      // Iter-146 — Customer-side: invoices + receipts linked to this project.
      // Invoices may carry a `project_id` directly (modern flow) or trace
      // back via `source_progress_claim_id`. Receipts link via invoice.
      try {
        const invAll = await axiosInstance.get("/invoices");
        const invList = Array.isArray(invAll.data) ? invAll.data
          : (invAll.data?.items || []);
        const projInvoices = invList.filter((i) => i.project_id === projectId);
        setCustomerInvoices(projInvoices);
        if (projInvoices.length) {
          const invoiceIds = new Set(projInvoices.map((i) => i.id));
          const payAll = await axiosInstance.get("/payments");
          const payList = Array.isArray(payAll.data) ? payAll.data
            : (payAll.data?.items || []);
          // Receipts for this project = payments tied to one of its
          // invoices, or directly to this project_id.
          setCustomerReceipts(payList.filter((p) =>
            p.project_id === projectId
            || (p.reference_type === "invoice" && invoiceIds.has(p.reference_id))
          ));
        } else {
          setCustomerReceipts([]);
        }
      } catch { setCustomerInvoices([]); setCustomerReceipts([]); }
    } catch (err) {
      toast.error("تعذّر تحميل بيانات المشروع");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) refreshAll(); });
    const t = setInterval(() => { if (!cancelled) refreshAll(); }, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [refreshAll]);

  // ── Derived KPIs ─────────────────────────────────────────────────
  const k = useMemo(() => {
    const budget    = Number(summary?.budget_baseline || 0);
    const committed = Number(summary?.committed_total || 0);
    const actual    = Number(summary?.actual_total || 0);
    const available = Number(
      summary?.available_to_commit ?? (budget - committed - actual),
    );
    const eac       = committed + actual;             // EAC-lite
    const variance  = budget - eac;                   // + = under, − = over
    const pctOfBudget = budget > 0 ? (eac / budget) * 100 : 0;
    const overCommitment = budget > 0 ? Math.max(0, (eac / budget - 1) * 100) : 0;
    return { budget, committed, actual, available, eac, variance,
              pctOfBudget, overCommitment };
  }, [summary]);

  const unbilledGrnExposure = useMemo(() => {
    // Real value comes from the GL via /commitment-summary (GR/IR open).
    if (summary?.gr_ir_open_balance != null) {
      return Number(summary.gr_ir_open_balance);
    }
    // Fallback: best-effort if backend hasn't published the figure yet.
    const billedPoIds = new Set(
      bills.filter((b) => b.approval_status === "approved")
            .map((b) => b.purchase_order_id),
    );
    let exposure = 0;
    grns.forEach((g) => {
      if (g.status !== "posted") return;
      if (billedPoIds.has(g.purchase_order_id)) return;
      (g.lines || []).forEach((li) => {
        exposure += Number(li.received_qty || 0) * Number(li.unit_price || 0);
      });
    });
    return exposure;
  }, [summary, grns, bills]);

  const pendingApprovals = useMemo(() => ({
    awards: awards.filter((a) => a.status === "submitted").length,
    bills:  bills.filter((b) => b.approval_status === "pending").length,
    payments: payments.filter((p) => p.status === "pending_authorization").length,
  }), [awards, bills, payments]);

  const sumAmount = (rows, key) =>
    rows.reduce((s, r) => s + Number(r?.[key] || 0), 0);

  const openCommitmentDetail = async (cid) => {
    setOpenCommitmentId(cid);
    setMovements([]);
    try {
      const r = await axiosInstance.get(`/commitments/${cid}`);
      setMovements(r.data?.movements || []);
    } catch {
      toast.error("تعذّر جلب حركات التعهد");
    }
  };

  const updateProgress = async () => {
    const current = evm?.progress_percent ?? 0;
    const inp = window.prompt(
      `نسبة الإنجاز الحالية: ${current}%\nأدخل النسبة الجديدة (0–100):`,
      String(current),
    );
    if (inp == null) return;
    const pct = Number(inp);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("نسبة غير صحيحة"); return;
    }
    try {
      await axiosInstance.post(`/projects/${projectId}/progress`,
                                { percent: pct });
      toast.success(`تم تحديث التقدم إلى ${pct}%`);
      refreshAll();
    } catch {
      toast.error("فشل تحديث التقدم");
    }
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center" data-testid="pch-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-10 text-center" data-testid="pch-empty">
        <p className="text-slate-600">المشروع غير موجود</p>
        <Link to="/projects" className="text-blue-600 underline">العودة</Link>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-1" dir="rtl" data-testid="project-control-hub">

      {/* 1. Hero strip ------------------------------------------------ */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between"
            data-testid="pch-hero">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/projects" className="inline-flex items-center gap-1 hover:text-slate-800">
              <ArrowLeft className="h-3.5 w-3.5" /> المشاريع
            </Link>
            <span>·</span>
            <span>Project Control Hub</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900"
              data-testid="pch-project-name">
            {project.name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Pill tone="blue">{project.client_name || "—"}</Pill>
            {summary?.contract_number && (
              <Pill tone="violet">{summary.contract_number}</Pill>
            )}
            <Pill tone={project.status === "in_progress" ? "green"
                       : project.status === "completed" ? "slate"
                       : project.status === "on_hold" ? "amber" : "blue"}>
              {project.status}
            </Pill>
          </div>
        </div>

        {/* EAC vs Budget compact summary */}
        <div className="grid grid-cols-2 gap-3 md:w-[420px]">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              EAC (متوقع)
            </div>
            <div className="mt-1 text-lg font-bold text-slate-900 tabular-nums"
                  data-testid="pch-eac">
              {SAR(k.eac)}
            </div>
          </div>
          <div className={`rounded-xl border p-3 text-center ${
              k.variance < 0
                ? "border-rose-200 bg-rose-50 text-rose-900"
                : "border-emerald-200 bg-emerald-50 text-emerald-900"
            }`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
              {k.variance < 0 ? "تجاوز" : "وفر"} vs Budget
            </div>
            <div className="mt-1 inline-flex items-center gap-1 text-lg font-bold tabular-nums"
                  data-testid="pch-variance">
              {k.variance < 0
                ? <TrendingDown className="h-4 w-4" />
                : <TrendingUp className="h-4 w-4" />}
              {SAR(Math.abs(k.variance))}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Core Financial Stack ------------------------------------- */}
      <div data-testid="pch-financial-stack">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold text-slate-800">المركز المالي للمشروع</h2>
          <span className="text-xs text-slate-500">
            مُحدّث: {summary?.computed_at
              ? new Date(summary.computed_at).toLocaleString("ar-SA")
              : "—"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KPI testid="pch-kpi-budget" label="Budget Baseline"
                value={SAR(k.budget)}
                sub={summary?.contract_number ? `من العقد ${summary.contract_number}` : "—"}
                tone="slate" icon={FileSignature} />
          <KPI testid="pch-kpi-committed" label="Committed"
                value={SAR(k.committed)}
                sub={`${summary?.committed_count || 0} تعهد فعّال`}
                tone="amber" icon={ShoppingCart} />
          <KPI testid="pch-kpi-actual" label="Actual (Bills)"
                value={SAR(k.actual)}
                sub={`${summary?.actual_count || 0} فاتورة معتمدة`}
                tone="blue" icon={Receipt} />
          <KPI testid="pch-kpi-available"
                label="Available to Commit"
                value={SAR(k.available)}
                sub={`${PCT(100 - k.pctOfBudget)} من الموازنة متاح`}
                tone={k.available < 0 ? "red" : "green"}
                icon={Wallet} />
        </div>

        {/* Master progress bar */}
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              data-testid="pch-budget-bar">
          <div className="mb-2 flex items-center justify-between text-xs text-slate-600">
            <span>استهلاك الموازنة (Actual + Committed / Budget)</span>
            <span className={`font-semibold ${
              k.pctOfBudget > 100 ? "text-rose-600"
                : k.pctOfBudget > 85 ? "text-amber-600"
                : "text-emerald-600"}`}>
              {PCT(k.pctOfBudget)}
            </span>
          </div>
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-100">
            {/* Actual */}
            <div
              className="absolute inset-y-0 right-0 bg-blue-500"
              style={{ width: `${Math.min(100, k.budget > 0 ? (k.actual / k.budget) * 100 : 0)}%` }}
            />
            {/* Committed (stacked) */}
            <div
              className="absolute inset-y-0 bg-amber-400"
              style={{
                right: `${Math.min(100, k.budget > 0 ? (k.actual / k.budget) * 100 : 0)}%`,
                width: `${Math.min(100, k.budget > 0 ? (k.committed / k.budget) * 100 : 0)}%`,
              }}
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-blue-500" /> Actual
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-amber-400" /> Committed
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-slate-100 ring-1 ring-slate-200" /> Available
            </span>
          </div>
        </div>
      </div>

      {/* 3. Execution Timeline --------------------------------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            data-testid="pch-timeline">
        <h2 className="mb-4 text-lg font-semibold text-slate-800">سير التنفيذ المالي</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <TimelineStep icon={FileSignature} label="Awards"
                         count={awards.length}
                         amount={sumAmount(awards, "total_value") || sumAmount(awards, "total")}
                         tone="violet" testid="pch-step-awards" />
          <TimelineStep icon={ShoppingCart} label="Purchase Orders"
                         count={pos.length}
                         amount={sumAmount(pos, "total")}
                         tone="amber" testid="pch-step-pos" />
          <TimelineStep icon={PackageCheck} label="GRN (مُستلم)"
                         count={grns.filter((g) => g.status === "posted").length}
                         amount={null}
                         tone="blue" testid="pch-step-grns"
                         sub={`${grns.length} إجمالي`} />
          <TimelineStep icon={Receipt} label="Bills (معتمدة)"
                         count={bills.filter((b) => b.approval_status === "approved").length}
                         amount={sumAmount(
                            bills.filter((b) => b.approval_status === "approved"),
                            "total",
                         )}
                         tone="green" testid="pch-step-bills" />
          <TimelineStep icon={Wallet} label="Payments"
                         count={payments.filter((p) => p.status === "paid"
                                || p.status === "authorized" || p.status === "completed").length}
                         amount={sumAmount(payments, "amount")}
                         tone="slate" testid="pch-step-payments" />
        </div>
      </div>

      {/* 3.B Customer-side flow (Iter-146): revenue cycle counterpart
          to the procurement cycle above. */}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm"
           data-testid="pch-customer-flow">
        <h2 className="mb-4 text-lg font-semibold text-slate-800">دورة العميل (الإيرادات)</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <TimelineStep icon={Receipt} label="فواتير العميل"
                         count={customerInvoices.length}
                         amount={sumAmount(customerInvoices, "total_with_vat") || sumAmount(customerInvoices, "total")}
                         tone="blue" testid="pch-step-customer-invoices" />
          <TimelineStep icon={Wallet} label="مقبوضات العميل"
                         count={customerReceipts.length}
                         amount={sumAmount(customerReceipts, "amount")}
                         tone="green" testid="pch-step-customer-receipts" />
          <TimelineStep icon={FileSignature} label="معتمدة في ZATCA"
                         count={customerInvoices.filter((i) =>
                            ["cleared", "reported", "accepted"].includes(
                              (i.zatca_status || "").toLowerCase()
                            )).length}
                         amount={null}
                         tone="violet" testid="pch-step-customer-zatca"
                         sub={`${customerInvoices.length} إجمالي`} />
          <TimelineStep icon={Wallet} label="ذمم العميل المتبقّية"
                         count={null}
                         amount={Math.max(0,
                            (sumAmount(customerInvoices, "total_with_vat") || sumAmount(customerInvoices, "total"))
                            - sumAmount(customerReceipts, "amount"))}
                         tone="amber" testid="pch-step-customer-outstanding" />
        </div>
      </div>

      {/* 4. Risk Indicators ------------------------------------------ */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="pch-risks">
        <RiskCard
          testid="pch-risk-overcommit"
          title="نسبة التجاوز"
          value={k.overCommitment > 0 ? `${PCT(k.overCommitment)}` : "0%"}
          severity={k.overCommitment > 10 ? "red" : k.overCommitment > 0 ? "amber" : "green"}
          note={k.overCommitment > 0
            ? "EAC تجاوز الموازنة المعتمدة"
            : "ضمن الموازنة"} />
        <RiskCard
          testid="pch-risk-unbilled-grn"
          title="GR/IR Open Balance"
          value={SAR(unbilledGrnExposure)}
          severity={unbilledGrnExposure > 0 ? "amber" : "green"}
          note={summary?.gr_ir_open_balance != null
            ? "من دفتر الأستاذ — استلام بدون فاتورة (IFRS accrual)"
            : "GRN معتمد لم تصل فاتورته بعد (GR-IR exposure)"} />
        <RiskCard
          testid="pch-risk-pending"
          title="معلّقة بانتظار الاعتماد"
          value={`${pendingApprovals.awards + pendingApprovals.bills + pendingApprovals.payments}`}
          severity={
            (pendingApprovals.awards + pendingApprovals.bills + pendingApprovals.payments) > 0
              ? "amber" : "green"
          }
          note={`Awards: ${pendingApprovals.awards} · Bills: ${pendingApprovals.bills} · Payments: ${pendingApprovals.payments}`} />
      </div>

      {/* 4.4 EVM Cockpit (Earned Value Management) --------------- */}
      {evm && evm.bac > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              data-testid="pch-evm">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-slate-800">
              Earned Value (CPI / SPI / EAC)
            </h2>
            <button
              type="button"
              data-testid="pch-evm-update-progress"
              onClick={updateProgress}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
            >
              تحديث نسبة الإنجاز · {evm.progress_percent}%
            </button>
          </div>
          {/* Progress bars: Planned vs Earned */}
          <div className="mb-4 space-y-2">
            <div>
              <div className="mb-1 flex justify-between text-[11px] text-slate-600">
                <span>المخطط (PV)</span>
                <span className="tabular-nums">{evm.planned_percent}% · {SAR(evm.pv)}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-slate-400"
                      style={{ width: `${Math.min(100, evm.planned_percent)}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex justify-between text-[11px] text-slate-600">
                <span>المُكتسب (EV)</span>
                <span className="tabular-nums">{evm.progress_percent}% · {SAR(evm.ev)}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-emerald-500"
                      style={{ width: `${Math.min(100, evm.progress_percent)}%` }} />
              </div>
            </div>
          </div>
          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <KPI testid="pch-evm-cpi" label="CPI"
                  value={evm.cpi != null ? evm.cpi.toFixed(2) : "—"}
                  sub={evm.cpi != null && evm.cpi >= 1
                    ? "ضمن التكلفة" : "تجاوز التكلفة"}
                  tone={evm.cpi_status === "good" ? "green"
                    : evm.cpi_status === "warn" ? "amber" : "red"} />
            <KPI testid="pch-evm-spi" label="SPI"
                  value={evm.spi != null ? evm.spi.toFixed(2) : "—"}
                  sub={evm.spi != null && evm.spi >= 1
                    ? "متقدم على الجدول" : "تأخر زمني"}
                  tone={evm.spi_status === "good" ? "green"
                    : evm.spi_status === "warn" ? "amber" : "red"} />
            <KPI testid="pch-evm-eac" label="EAC"
                  value={SAR(evm.eac)}
                  sub="تكلفة متوقعة عند الإنجاز" tone="slate" />
            <KPI testid="pch-evm-etc" label="ETC"
                  value={SAR(evm.etc)}
                  sub="المتبقي للإنجاز" tone="slate" />
            <KPI testid="pch-evm-vac" label="VAC"
                  value={SAR(evm.vac)}
                  sub={evm.vac >= 0 ? "وفر متوقع" : "تجاوز متوقع"}
                  tone={evm.vac >= 0 ? "green" : "red"} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 text-[11px] text-slate-500">
            <div>BAC: <span className="font-semibold text-slate-700 tabular-nums">{SAR(evm.bac)}</span></div>
            <div>AC: <span className="font-semibold text-slate-700 tabular-nums">{SAR(evm.ac)}</span></div>
            <div>CV: <span className={`font-semibold tabular-nums ${evm.cv >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{SAR(evm.cv)}</span></div>
            <div>SV: <span className={`font-semibold tabular-nums ${evm.sv >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{SAR(evm.sv)}</span></div>
          </div>
        </div>
      )}

      {/* 4.45 Revenue Surface (P4.4) ----------------------------- */}
      {summary && (summary.revenue_recognized > 0
                    || summary.retention_held > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              data-testid="pch-revenue">
          <h2 className="mb-3 text-lg font-semibold text-slate-800">
            الإيرادات والربحية
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KPI testid="pch-rev-recognized" label="Revenue Recognized"
                  value={SAR(summary.revenue_recognized)}
                  sub="من Progress Claims معتمدة" tone="green" />
            <KPI testid="pch-rev-retention" label="Retention Held"
                  value={SAR(summary.retention_held)}
                  sub="محتجز ضمان" tone="amber" />
            <KPI testid="pch-rev-profit" label="Projected Profit"
                  value={SAR(summary.projected_profit)}
                  sub={`Revenue − Actual Cost`}
                  tone={summary.projected_profit >= 0 ? "green" : "red"} />
            <KPI testid="pch-rev-margin" label="Gross Margin"
                  value={summary.gross_margin_pct != null
                    ? `${summary.gross_margin_pct}%` : "—"}
                  sub="هامش ربح إجمالي"
                  tone={summary.gross_margin_pct == null ? "slate"
                    : summary.gross_margin_pct >= 15 ? "green"
                    : summary.gross_margin_pct >= 5 ? "amber" : "red"} />
          </div>
        </div>
      )}

      {/* 4.5 GR/IR Aging — the auditor's first question --------- */}
      {aging && aging.summary && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              data-testid="pch-grir-aging">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-slate-800">
              تقادم GR/IR (استلام بدون فوترة)
            </h2>
            <span className="text-xs text-slate-500">
              مجموع مفتوح: <span className="font-semibold tabular-nums">
                {SAR(aging.summary.total_open_balance)}
              </span> · {aging.summary.open_positions} مركز
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {aging.buckets.map((b, idx) => {
              const tones = ["green", "amber", "amber", "red"];
              const labels = {
                "0_30":    "0 — 30 يوم",
                "31_60":   "31 — 60 يوم",
                "61_90":   "61 — 90 يوم",
                "90_plus": "+90 يوم",
              };
              return (
                <div key={b.label}
                      data-testid={`pch-aging-bucket-${b.label}`}
                      className={`rounded-xl border p-3 ${
                        tones[idx] === "red"   ? "border-rose-200 bg-rose-50"
                        : tones[idx] === "amber" ? "border-amber-200 bg-amber-50"
                        : "border-emerald-200 bg-emerald-50"
                      }`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
                    {labels[b.label] || b.label}
                  </div>
                  <div className="mt-1 text-xl font-bold tabular-nums">
                    {SAR(b.total)}
                  </div>
                  <div className="mt-0.5 text-[10px] opacity-60">
                    {b.count} مركز
                  </div>
                </div>
              );
            })}
          </div>
          {aging.rows && aging.rows.length > 0 && (
            <div className="mt-4 max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-right text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">PO #</th>
                    <th className="px-3 py-2 font-semibold">المورد</th>
                    <th className="px-3 py-2 font-semibold tabular-nums">المفتوح</th>
                    <th className="px-3 py-2 font-semibold tabular-nums">أيام</th>
                    <th className="px-3 py-2 font-semibold">منذ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {aging.rows.slice(0, 30).map((r) => (
                    <tr key={r.po_id} data-testid={`pch-aging-row-${r.po_id}`}>
                      <td className="px-3 py-2 font-mono">{r.po_number}</td>
                      <td className="px-3 py-2">{r.supplier_name || "—"}</td>
                      <td className="px-3 py-2 tabular-nums font-semibold">
                        {SAR(r.open_balance)}
                      </td>
                      <td className="px-3 py-2 tabular-nums">{r.days_open}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {r.earliest_grn_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 5. Drilldown — Commitments ---------------------------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            data-testid="pch-commitments-table">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-800">
            <Layers className="ml-2 inline h-5 w-5 text-slate-400" />
            تعهدات المشروع
          </h2>
          <span className="text-xs text-slate-500">{commitments.length} سجل</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-right text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">CMT #</th>
                <th className="px-4 py-3 font-semibold">PO #</th>
                <th className="px-4 py-3 font-semibold">المورد</th>
                <th className="px-4 py-3 font-semibold tabular-nums">القيمة</th>
                <th className="px-4 py-3 font-semibold tabular-nums">المستهلك</th>
                <th className="px-4 py-3 font-semibold tabular-nums">المتبقي</th>
                <th className="px-4 py-3 font-semibold">الحالة</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {commitments.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-400">
                    لا توجد تعهدات بعد لهذا المشروع.
                  </td>
                </tr>
              )}
              {commitments.map((c) => {
                const pct = c.amount_committed > 0
                  ? (c.amount_relieved / c.amount_committed) * 100
                  : 0;
                const tone = c.status === "fully_relieved" ? "green"
                  : c.status === "partially_relieved" ? "amber"
                  : c.status === "cancelled" ? "slate" : "blue";
                return (
                  <tr key={c.id} data-testid={`pch-cmt-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{c.commitment_no}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.po_number}</td>
                    <td className="px-4 py-3">{c.supplier_name || "—"}</td>
                    <td className="px-4 py-3 tabular-nums">{SAR(c.amount_committed)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-500">
                      {SAR(c.amount_relieved)} <span className="text-[10px]">({PCT(pct)})</span>
                    </td>
                    <td className="px-4 py-3 tabular-nums font-semibold">
                      {SAR(c.amount_remaining)}
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={tone}>{c.status}</Pill>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`pch-cmt-detail-${c.id}`}
                        onClick={() => openCommitmentDetail(c.id)}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                      >
                        التفاصيل
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drilldown Modal */}
      {openCommitmentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
              onClick={() => setOpenCommitmentId(null)}
              data-testid="pch-cmt-modal">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">حركات التعهد</h3>
              <button onClick={() => setOpenCommitmentId(null)}
                       className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <table className="w-full text-sm">
              <thead className="text-right text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">النوع</th>
                  <th className="py-2">المصدر</th>
                  <th className="py-2 tabular-nums">المبلغ</th>
                  <th className="py-2 tabular-nums">الرصيد</th>
                  <th className="py-2">الوقت</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td className="py-2">
                      <Pill tone={
                        m.movement_type === "created" ? "blue"
                        : m.movement_type === "cancelled" ? "slate"
                        : "amber"}>
                        {m.movement_type}
                      </Pill>
                    </td>
                    <td className="py-2 font-mono text-xs">
                      {m.source_doc_no || m.source_event}
                    </td>
                    <td className={`py-2 tabular-nums ${m.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                      {m.amount > 0 ? "+" : ""}{SAR(m.amount)}
                    </td>
                    <td className="py-2 tabular-nums">{SAR(m.running_balance)}</td>
                    <td className="py-2 text-xs text-slate-500">
                      {new Date(m.ts).toLocaleString("ar-SA")}
                    </td>
                  </tr>
                ))}
                {!movements.length && (
                  <tr><td colSpan="5" className="py-6 text-center text-slate-400">
                    لا توجد حركات.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Small helpers ──────────────────────────────────────────────────────

const TimelineStep = ({ icon: Icon, label, count, amount, sub, tone, testid }) => {
  const tones = {
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    blue:   "bg-blue-50 text-blue-700 border-blue-200",
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate:  "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div data-testid={testid}
         className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline justify-between">
        <span className="text-2xl font-bold tabular-nums">{count}</span>
        {amount != null && (
          <span className="text-xs opacity-80 tabular-nums">{SAR(amount)}</span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-[10px] opacity-60">{sub}</div>}
    </div>
  );
};

const RiskCard = ({ title, value, severity, note, testid }) => {
  const tones = {
    red:   "border-rose-200 bg-rose-50 text-rose-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  const Icon = severity === "red" ? AlertTriangle
    : severity === "amber" ? CircleDashed : CheckCircle2;
  return (
    <div data-testid={testid}
         className={`rounded-2xl border p-4 shadow-sm ${tones[severity]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-semibold uppercase tracking-wider opacity-80">
          {title}
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] opacity-80">{note}</div>
    </div>
  );
};

export default ProjectControlHub;
